import { useRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { mascaraDaRolagem, useRolagemHorizontal } from '../core/rolagem';

export interface SegmentoOption {
    value: string;
    label: ReactNode;
    icon?: ReactNode;
}

export interface SegmentedControlProps {
    options?: SegmentoOption[];
    value?: string;
    onChange?: (value: string) => void;
    size?: 'sm' | 'md';
    /** Nome do grupo para leitores de tela. */
    ariaLabel?: string;
    style?: CSSProperties;
}

export function SegmentedControl({ options = [], value, onChange, size = 'md', ariaLabel, style }: SegmentedControlProps) {
    const h = size === 'sm' ? 28 : 32;
    const grupo = useRef<HTMLDivElement | null>(null);
    const rolagem = useRolagemHorizontal(grupo, options.length);
    /* Nunca mais largo que o espaço: com rótulos longos ("Aprovados (1)",
       "Pacotes (1)") a 320 px o controle empurrava o <main> a rolar de lado, calado.
       Agora rola dentro de si e esmaece a borda onde há mais opções. */
    return (
        <div
            ref={grupo} role="group" aria-label={ariaLabel} data-rolagem="horizontal"
            style={{
                display: 'inline-flex', maxWidth: '100%', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none',
                padding: 2, gap: 2, background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-sm)',
                ...mascaraDaRolagem(rolagem, 24), ...style,
            }}
        >
            {options.map((o) => {
                const ativo = o.value === value;
                return (
                    <button
                        key={o.value} type="button" aria-pressed={ativo}
                        onClick={() => onChange?.(o.value)}
                        style={{
                            height: h, padding: '0 12px', border: 0, borderRadius: 6, cursor: 'pointer', flex: '0 0 auto', whiteSpace: 'nowrap',
                            background: ativo ? 'var(--surface)' : 'transparent',
                            boxShadow: ativo ? 'var(--shadow-xs)' : 'none',
                            color: ativo ? 'var(--text-primary)' : 'var(--text-secondary)',
                            fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: ativo ? 600 : 500,
                            transition: 'var(--transition-control)', display: 'flex', alignItems: 'center', gap: 6,
                        }}
                    >
                        {o.icon}{o.label}
                    </button>
                );
            })}
        </div>
    );
}
